// USERS AND PERMISSIONS > PARTNERS (owner, 2026-10-15: "Partners not showing
// in the list of users and permissions").
//
// The Users tab lists the accounts filed under YOUR company, and a partner's
// account sits under THEIR company - or does not exist at all for somebody
// only named on a roster - so they never appeared. Its save (PUT
// /permissions/:id) refuses anyone outside your company, by design, and its
// ticks are drawn against YOUR company's ceiling, which is not the limit a
// partner works under.
//
// So partners get their own tab, read from GET /partner-directory/accounts:
// every partner person on your projects, grouped by partner company, each
// with their own company's ceiling, and edited through the same Access dialog
// the Partners page uses (partner-directory routes, never /permissions/:id).
// Somebody with no account yet gets "invite them from Partners" instead of an
// editor; administrator accounts are shown and not edited from here. Which
// coordination tabs a whole partner company gets on a project is the
// Coordination access switch on the Partners page, linked from here.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Collapse, Empty, Input, Skeleton, Table, Tag, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { getPartnerAccounts } from "api/partners/coordinationGrantsApi";
import PartnerAccessDialog from "pages/Partners/PartnerAccessDialog";

const KIND = { person: "Person", supervisor: "Supervisor", executive: "Executive" };
const VIEW_LABEL = {
  shopDrawingView: "Shop drawings",
  constructionTrackingView: "Tracking",
  levelsAreasView: "Levels & areas",
  technicalDocsView: "Technical docs",
  sheetSearchView: "Find in sheets",
  rfiView: "RFIs",
};

export function CeilingCell({ ceiling }) {
  if (!ceiling) {
    return <Tooltip title="Their company has no permission list of its own, so nothing is capped by it"><Tag>Not capped</Tag></Tooltip>;
  }
  const views = ceiling.coordinationViews || [];
  const missing = Object.keys(VIEW_LABEL).filter((k) => !views.includes(k));
  return (
    <Tooltip title={missing.length
      ? `Their company's own permissions leave out: ${missing.map((k) => VIEW_LABEL[k]).join(", ")} - those tabs stay hidden for them whatever you switch on`
      : "Their company's own permissions allow every coordination tab"}>
      <Tag color={missing.length ? "orange" : "green"}>{ceiling.size} keys{missing.length ? `, ${missing.length} tab${missing.length === 1 ? "" : "s"} capped` : ""}</Tag>
    </Tooltip>
  );
}

export default function PartnerPeoplePanel() {
  const [state, setState] = useState({ loading: true, orgs: [], error: "" });
  const [query, setQuery] = useState("");
  const [access, setAccess] = useState(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    getPartnerAccounts()
      .then((r) => setState({ loading: false, orgs: r.orgs || [], error: "" }))
      .catch((e) => setState({ loading: false, orgs: [], error: e.message || "Could not read your partners" }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const orgs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return state.orgs;
    return state.orgs
      .map((o) => ({ ...o, people: o.people.filter((p) => `${p.name} ${p.email} ${o.name}`.toLowerCase().includes(q)) }))
      .filter((o) => o.people.length);
  }, [state.orgs, query]);

  const columns = [
    { title: "Name", key: "name", render: (_, p) => <span>{p.name || p.email}<div style={{ fontSize: 12, color: "#667085" }}>{p.email}</div></span> },
    { title: "On the roster as", key: "kinds", render: (_, p) => (p.kinds || []).map((k) => <Tag key={k}>{KIND[k] || k}</Tag>) },
    { title: "Projects", key: "projects", render: (_, p) => (p.projects || []).join(", ") },
    {
      title: "Account", key: "account",
      render: (_, p) => (p.account
        ? <span><Tag color={p.account.kind === "partner" ? "blue" : "gold"}>{p.account.label}</Tag>{p.account.companyName ? <span style={{ fontSize: 12 }}>{p.account.companyName}</span> : null}</span>
        : <Tag>No account yet</Tag>),
    },
    { title: "Their company's limit", key: "ceiling", render: (_, p) => (p.account ? <CeilingCell ceiling={p.ceiling} /> : "-") },
    {
      title: "", key: "act", width: 200,
      render: (_, p) => (p.editable
        ? <Button size="small" onClick={() => setAccess({ email: p.email, name: p.name })}>Permissions</Button>
        : (
          <span style={{ fontSize: 12, color: "#667085" }}>
            {p.why}
            {!p.account ? <> {" "}<Link to="/partners?view=all">Partners</Link></> : null}
          </span>
        )),
    },
  ];

  return (
    <div style={{ padding: 12 }} data-testid="partner-people-panel">
      <Alert
        type="info" showIcon style={{ marginBottom: 10 }}
        message="People from your partner companies on your projects"
        description={(
          <span>
            Each person works under their own company&apos;s limits, shown here. Which coordination tabs a whole partner
            company gets on a project is set under <Link to="/partners?view=all">Partners</Link> &gt; Coordination access.
          </span>
        )}
      />
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Input.Search allowClear size="small" placeholder="Find a partner person or company" value={query}
                      onChange={(e) => setQuery(e.target.value)} style={{ width: 300 }} />
        <Button size="small" onClick={load}>Refresh</Button>
      </div>
      {state.error ? <Alert type="error" showIcon message={state.error} /> : null}
      {state.loading ? <Skeleton active /> : null}
      {!state.loading && !state.error && !orgs.length ? (
        <Empty description={query ? "Nobody matches" : "No partner people on your projects yet"} />
      ) : null}
      {!state.loading && orgs.length ? (
        <Collapse
          defaultActiveKey={orgs.map((o) => o.companyId)}
          items={orgs.map((o) => ({
            key: o.companyId,
            label: `${o.name || "(no name)"} - ${o.people.length} ${o.people.length === 1 ? "person" : "people"}`,
            children: <Table size="small" rowKey="email" dataSource={o.people} columns={columns} pagination={{ pageSize: 25, hideOnSinglePage: true }} />,
          }))}
        />
      ) : null}
      {access ? (
        <PartnerAccessDialog key={access.email} email={access.email} name={access.name} onClose={() => { setAccess(null); load(); }} />
      ) : null}
    </div>
  );
}
